import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Play, Bookmark, BookmarkCheck, Eye, EyeOff, Star, PenLine, Share2, MessagesSquare, Tv } from "lucide-react";
import { api, fmtRuntime, fmtDate, trackEvent } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Loading, ErrorBox } from "@/components/Common";
import { RatingsPanel } from "@/components/RatingsPanel";
import { WhereToWatch } from "@/components/WhereToWatch";
import { Reviews, Discussion } from "@/components/Reviews";
import { Row } from "@/components/Row";
import { VideoCard, VideoPlayer } from "@/components/VideoCard";
import { CollectionPicker } from "@/components/CollectionPicker";
import { useTitleActions, TrailerModal } from "@/components/TitleActions";
import NotFound from "@/pages/NotFound";

const LABEL = { movie: "Movies", tv: "TV Shows", anime: "Anime" };
const LIST = { movie: "/movies", tv: "/tv-shows", anime: "/anime" };

function Fact({ k, v }) {
  if (!v || (Array.isArray(v) && !v.length)) return null;
  return <div><dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{k}</dt><dd className="mt-0.5 text-sm text-slate-200">{Array.isArray(v) ? v.join(", ") : v}</dd></div>;
}

function ActionBar({ t, mine }) {
  const { state, toggle } = useTitleActions(t, mine);
  const [trailer, setTrailer] = useState(null);
  const share = async () => {
    const url = window.location.origin + window.location.pathname;
    trackEvent("share", { title_id: t.id });
    if (navigator.share) { try { await navigator.share({ title: t.title, text: `${t.title} on Shivam Cine Core`, url }); } catch {} return; }
    await navigator.clipboard?.writeText(url); toast.success("Link copied to clipboard");
  };
  const btn = "flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm hover:border-amber-400 hover:text-amber-300 transition-colors";
  return (
    <div className="mt-6 flex flex-wrap gap-2.5" data-testid="title-actions">
      {t.trailer_key ? <button data-testid="watch-trailer-btn" onClick={() => setTrailer(t.trailer_key)} className="flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-black hover:bg-amber-300"><Play className="h-4 w-4 fill-black" />Watch Trailer</button>
        : <span className="rounded-full border border-white/10 px-4 py-2.5 text-sm text-slate-500">Trailer unavailable</span>}
      <a href="#where-to-watch" data-testid="where-to-watch-btn" className="flex items-center gap-2 rounded-full bg-purple-600 px-5 py-2.5 text-sm font-bold hover:bg-purple-500"><Tv className="h-4 w-4" />Where to Watch</a>
      <button data-testid="watchlist-btn" onClick={() => toggle("watchlist")} className={btn}>{state.watchlist ? <><BookmarkCheck className="h-4 w-4 text-amber-400" />In Watchlist</> : <><Bookmark className="h-4 w-4" />Add to Watchlist</>}</button>
      <button data-testid="watched-btn" onClick={() => toggle("watched")} className={btn}>{state.watched ? <><Eye className="h-4 w-4 text-emerald-400" />Watched</> : <><EyeOff className="h-4 w-4" />Mark as Watched</>}</button>
      <a href="#rate" data-testid="rate-btn" className={btn}><Star className="h-4 w-4" />Rate</a>
      <a href="#reviews" data-testid="review-btn" className={btn}><PenLine className="h-4 w-4" />Write a Review</a>
      <button data-testid="share-btn" onClick={share} className={btn}><Share2 className="h-4 w-4" />Share</button>
      <a href="#discussion" data-testid="join-discussion-btn" className={btn}><MessagesSquare className="h-4 w-4" />Join Discussion</a>
      <CollectionPicker titleId={t.id} />
      <TrailerModal trailerKey={trailer} onClose={() => setTrailer(null)} />
    </div>
  );
}

export default function TitlePage({ typePath }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [summary, setSummary] = useState(null);
  const [mineRating, setMineRating] = useState(null);
  const [mineReview, setMineReview] = useState(null);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    setD(null); setErr(null);
    api.get(`/title/${typePath}/${slug}`).then(({ data }) => {
      if (data.redirect) return nav(data.redirect + window.location.search, { replace: true });
      setD(data); setSummary(data.rating); setMineRating(data.mine.rating || null); setMineReview(data.mine.review || null);
      if (!data.preview) trackEvent("page_view", { title_id: data.title.id });
      if (window.location.hash) setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }), 400);
    }).catch((e) => setErr(e.response?.status === 404 ? 404 : 500));
  }, [typePath, slug, nav]);

  const t = d?.title;
  const jsonLd = useMemo(() => t && [{
    "@context": "https://schema.org", "@type": t.type === "movie" ? "Movie" : "TVSeries", name: t.title, image: t.poster, description: t.synopsis,
    datePublished: t.release_date, genre: t.genres, inLanguage: t.language, url: window.location.href,
    director: t.directors?.map((n) => ({ "@type": "Person", name: n })), actor: t.cast?.slice(0, 5).map((c) => ({ "@type": "Person", name: c.name })),
    ...(t.imdb_id ? { sameAs: `https://www.imdb.com/title/${t.imdb_id}/` } : {}),
  }, { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
    { "@type": "ListItem", position: 2, name: LABEL[t.type], item: window.location.origin + LIST[t.type] },
    { "@type": "ListItem", position: 3, name: t.title } ] }], [t]);
  useSeo(t ? { title: t.seo?.title || `${t.title} (${t.year}) — Where to Watch, Reviews & Ratings`, description: t.seo?.description || t.synopsis?.slice(0, 160),
    image: t.seo?.image || t.backdrop || t.poster, noindex: t.seo?.noindex || d.preview, jsonLd } : { title: "Loading" });

  if (err === 404) return <NotFound />;
  if (err) return <ErrorBox text="This page couldn't load. Please try again shortly." />;
  if (!d) return <Loading />;

  return (
    <article data-testid="title-page">
      {d.preview && <div className="bg-purple-700 px-4 py-2 text-center text-sm font-semibold" data-testid="preview-banner">Admin preview — this title is {t.status} and not visible to the public.</div>}
      <div className="relative">
        <div className="absolute inset-0 h-[70vh] overflow-hidden">
          <img src={t.backdrop || t.poster} alt="" className={`h-full w-full object-cover ${t.backdrop ? "opacity-50" : "opacity-25 blur-2xl scale-110"}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/80 to-[#0A0A0E]/30" />
        </div>
        <div className="relative px-4 pt-6 sm:px-6 lg:px-12">
          <nav aria-label="Breadcrumb" className="text-xs text-slate-400" data-testid="breadcrumb"><Link to="/" className="hover:text-amber-400">Home</Link> / <Link to={LIST[t.type]} className="hover:text-amber-400">{LABEL[t.type]}</Link> / <span className="text-slate-200">{t.title}</span></nav>
          <div className="mt-6 grid gap-8 md:grid-cols-[240px_1fr] lg:grid-cols-[300px_1fr]">
            <img src={t.poster} alt={`${t.title} (${t.year}) poster`} className="mx-auto w-48 rounded-2xl border border-white/10 shadow-2xl md:w-full fade-up" />
            <div className="fade-up">
              <p className="eyebrow">{t.type === "tv" ? "Series" : t.type === "anime" ? "Anime" : "Movie"} {t.content_rating && `· Rated ${t.content_rating}`}</p>
              <h1 className="font-display text-5xl leading-[0.9] sm:text-6xl lg:text-7xl" data-testid="title-heading">{t.title} <span className="text-slate-500">({t.year})</span></h1>
              {t.original_title && t.original_title !== t.title && <p className="mt-1 text-sm text-slate-400">Original title: {t.original_title}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-300">
                {t.release_date && <span>{fmtDate(t.release_date)}</span>}
                {t.runtime > 0 && <span>· {fmtRuntime(t.runtime)}{t.type !== "movie" ? " per episode" : ""}</span>}
                {t.genres?.map((g) => <Link key={g} to={`${LIST[t.type]}?genre=${encodeURIComponent(g)}`} className="rounded-full border border-white/15 px-2.5 py-0.5 text-xs hover:border-amber-400">{g}</Link>)}
              </div>
              <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-300" data-testid="synopsis">{t.synopsis || "Synopsis coming soon."}</p>
              <ActionBar t={t} mine={d.mine} />
              <dl className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                <Fact k={t.type === "movie" ? "Director" : "Created by"} v={t.directors} /><Fact k="Writers" v={t.writers} /><Fact k="Country" v={t.country} />
                <Fact k="Language" v={t.language} /><Fact k="Production" v={t.companies} />
                <Fact k="Seasons" v={t.seasons?.length ? t.seasons.map((s) => `S${s.number}: ${s.episodes} ep`).join(" · ") : null} />
                <Fact k="External IDs" v={[t.imdb_id && `IMDb ${t.imdb_id}`, t.tmdb_id && `TMDB ${t.tmdb_id}`].filter(Boolean)} />
              </dl>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-14 px-4 pt-12 sm:px-6 lg:px-12">
        <WhereToWatch title={t} links={d.links} />
        <RatingsPanel title={t} summary={summary} setSummary={setSummary} mine={mineRating} setMine={setMineRating} />
        {t.cast?.length > 0 && (
          <section data-testid="cast-section">
            <h2 className="mb-4 font-display text-4xl">Cast</h2>
            <div className="no-scrollbar flex gap-4 overflow-x-auto pb-2">
              {t.cast.map((c) => (
                <Link key={c.name} to={`/movies?q=${encodeURIComponent(c.name)}`} className="w-28 shrink-0 text-center">
                  {c.photo ? <img src={c.photo} alt={c.name} loading="lazy" className="mx-auto h-28 w-28 rounded-full object-cover" /> : <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-purple-900 to-[#181824] font-display text-3xl text-amber-400">{c.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>}
                  <p className="mt-2 text-sm font-semibold">{c.name}</p>{c.character && <p className="text-xs text-slate-500">{c.character}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}
        {d.videos.length > 0 && (
          <section data-testid="related-videos">
            <p className="eyebrow">From Shivam</p><h2 className="mb-4 font-display text-4xl">Explained & Recommended</h2>
            <div className="no-scrollbar flex gap-4 overflow-x-auto pb-2">{d.videos.map((v) => <VideoCard key={v.id} v={v} onPlay={setPlaying} />)}</div>
            <VideoPlayer v={playing} onClose={() => setPlaying(null)} />
          </section>
        )}
        <div className="grid gap-12 lg:grid-cols-2">
          <Reviews title={t} mine={mineReview} onMine={setMineReview} />
          <Discussion title={t} />
        </div>
        <p className="text-[11px] text-slate-600">Metadata source: {t.metadata_source || "manual"}{t.last_synced_at ? ` · last synced ${fmtDate(t.last_synced_at)}` : ""}</p>
      </div>
      <div className="-mx-0 mt-6"><Row testid="similar-row" title="More Like This" items={d.similar} empty="No similar titles yet." /></div>
    </article>
  );
}
